import React from 'react';
import DashedLine from 'react-native-dashed-line';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import ScreenContext from '../../ScreenContext';
import AppRoutes from 'src/AppRoutes';
import {getTaxDescription} from 'src/services/TranslationService';
import AVFormattedCurrency from '../../elements/AVFormattedCurrency';
import ReceiptItem from '../../elements/cart/ReceiptItem';
import Styles from '../../Styles';
import BaseSummaryScreen from '../account/history/BaseSummaryScreen';
import Settings from 'src/Settings';
import AVText from '../../elements/AVText';
import {PixelRatio, StyleSheet, View} from 'react-native';
import BuildTypes from 'src/constants/BuildTypeConstants';
import Localized from 'src/constants/AppStrings';
import {NavigationProp} from '@react-navigation/native';
import {getPreviousRouteName} from 'src/Util';
import {getDescriber} from './descriptor/Descriptor';
import AccountStore from 'src/stores/AccountStore';
import FirebaseAnalytic from '../../../nativeModules/FirebaseAnalytic';
import Events from 'src/logging/Events';
import {withGlobalize} from '../../../i18n/HOC/withGlobalize';

type ReceiptScreenProps = {
  returnRoute: AppRoutes;
  navigation?: NavigationProp<ReceiptScreen>;
  balance: number;
  ccDisplay: string;
  totalDue: number;
  subtotal: number;
  discount: number;
  taxes: Array<any>;
  deposit: number;
  items: Array<any>;
  points: number;
  split: boolean;
  insideTab: boolean;
  deviceId: string;
  supFee: number;
  deviceName: string;
  deviceAddress: string;
  transactionId: string;
};

type ReceiptScreenState = {
  previousRoute: string | null;
};

class ReceiptScreen extends React.Component<
  ReceiptScreenProps,
  ReceiptScreenState
> {
  static contextType = ScreenContext;
  declare context: React.ContextType<typeof ScreenContext>;

  constructor(props) {
    super(props);
    this.state = {
      previousRoute: null,
    };
    this.renderSummary = this.renderSummary.bind(this);
  }

  computeTotalAmount() {
    // Calculate total taxes
    const totalTaxes =
      this.props.taxes?.reduce(
        (acc, tax) => acc + parseFloat(tax.TaxAmount),
        0,
      ) || 0;

    // Calculate total amount
    const totalAmt =
      (this.props?.subtotal || 0) + totalTaxes - (this.props?.discount || 0);

    return totalAmt;
  }

  componentDidMount(): void {
    FirebaseAnalytic.trackEvent('componentDidMount', 'ReceiptScreen', {
      ...this.props,
      ...this.state,
    });

    Events.Info.trackEvent('ReceiptScreen', this.props);

    const previousRoute = getPreviousRouteName(
      this.props.navigation?.getState()?.routes,
    );

    this.setState({previousRoute});
  }

  renderHeader() {
    return (
      <View style={getDescriber().getReceiptStyles().header}>
        <View style={{paddingBottom: Styles.Spacing.m2}}>
          <AVText style={getDescriber().getReceiptStyles().deviceName}>
            {this.props.deviceName}
          </AVText>
          <AVText style={getDescriber().getReceiptStyles().deviceAddress}>
            {this.props.deviceAddress}
          </AVText>
        </View>
        <View style={{paddingBottom: Styles.Spacing.m2}}>
          <AVText style={getDescriber().getReceiptStyles().dateTime}>
            {new Date().toLocaleString(Settings.getLocale(), {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              hour12: true,
            })}
          </AVText>
          <AVText style={getDescriber().getReceiptStyles().transactionId}>
            {this.props.transactionId}
          </AVText>
        </View>
      </View>
    );
  }

  renderSummary() {
    const labels: JSX.Element[] = [];
    const amounts: JSX.Element[] = [];
    if (this.props.supFee && this.props.supFee > 0) {
      labels.push(
        <AVText
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
          style={[
            getDescriber().getReceiptStyles().labelStyle,
            getDescriber().getReceiptStyles().supFeeStyle,
          ]}
          key="sup"
          accessibilityElementsHidden={true}
          importantForAccessibility="no-hide-descendants"
        >
          {Localized.Labels.sup_fee}
        </AVText>,
      );
      amounts.push(
        <AVFormattedCurrency
          accessible={true}
          accessibilityLabel={`${Localized.Labels.sup_fee} $${this.props.supFee}`}
          aria-label={`${Localized.Labels.sup_fee} $${this.props.supFee}, text`}
          value={this.props.supFee}
          key="sup"
          style={[
            getDescriber().getReceiptStyles().labelStyle,
            getDescriber().getReceiptStyles().supFeeStyle,
          ]}
          currency={AccountStore.getCurrency()}
        />,
      );
    }

    if (this.props.subtotal > 0) {
      labels.push(
        <AVText
          maxFontSizeMultiplier={1.0}
          accessibilityElementsHidden={true}
          importantForAccessibility="no-hide-descendants"
          style={getDescriber().getReceiptStyles().labelStyle}
          key="subtotal"
        >
          {Localized.Labels.subtotal}
        </AVText>,
      );
      amounts.push(
        <AVFormattedCurrency
          accessibilityLabel={`${Localized.Labels.subtotal} $${this.props.subtotal}`}
          accessibilityRole="text"
          aria-label={`${Localized.Labels.subtotal} $${this.props.subtotal}, text`}
          accessible={true}
          key="subtotal"
          style={getDescriber().getReceiptStyles().labelStyle}
          value={this.props.subtotal}
          currency={AccountStore.getCurrency()}
        />,
      );
    }

    if (this.props.discount > 0) {
      labels.push(
        <AVText
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
          style={[
            getDescriber().getReceiptStyles().labelStyle,
            getDescriber().getReceiptStyles().discount,
          ]}
          key="discount"
          accessibilityElementsHidden={true}
          importantForAccessibility="no-hide-descendants"
        >
          {Localized.Labels.discount}
        </AVText>,
      );
      amounts.push(
        <AVFormattedCurrency
          accessible={true}
          accessibilityLabel={`${Localized.Labels.discount} $${
            this.props.discount * -1
          }`}
          aria-label={`${Localized.Labels.discount} $${
            this.props.discount * -1
          }, text`}
          value={this.props.discount * -1}
          key="discount"
          style={[
            getDescriber().getReceiptStyles().labelStyle,
            getDescriber().getReceiptStyles().discount,
          ]}
          currency={AccountStore.getCurrency()}
        />,
      );
    }

    if (this.props.taxes && this.props.taxes.length > 0) {
      for (let i = 0; i < this.props.taxes.length; i++) {
        const tax = this.props.taxes[i];
        labels.push(
          <AVText
            maxFontSizeMultiplier={1.0}
            accessibilityElementsHidden={true}
            importantForAccessibility="no-hide-descendants"
            style={getDescriber().getReceiptStyles().labelStyle}
            key={`tax${i}`}
          >
            {getTaxDescription(Localized, tax.Description)}
          </AVText>,
        );
        amounts.push(
          <AVFormattedCurrency
            value={parseFloat(tax.TaxAmount)}
            style={getDescriber().getReceiptStyles().labelStyle}
            accessible={true}
            accessibilityLabel={`${getTaxDescription(
              Localized,
              tax.Description,
            )} $${parseFloat(tax.TaxAmount)}`}
            accessibilityRole="text"
            aria-label={`${getTaxDescription(
              Localized,
              tax.Description,
            )} $${parseFloat(tax.TaxAmount)}, text`}
            key={`tax${i}`}
            currency={AccountStore.getCurrency()}
          />,
        );
      }
    }

    if (this.props.supFee && this.props.supFee > 0) {
      labels.push(
        <AVText
          maxFontSizeMultiplier={1.0}
          accessibilityElementsHidden={true}
          importantForAccessibility="no-hide-descendants"
          style={getDescriber().getReceiptStyles().labelStyle}
          key="sup_fee"
        >
          {Localized.Labels.sup_fee}
        </AVText>,
      );
      amounts.push(
        <AVFormattedCurrency
          accessibilityLabel={`${Localized.Labels.sup_fee} $${this.props.supFee}`}
          accessibilityRole="text"
          aria-label={`${Localized.Labels.sup_fee} $${this.props.supFee}, text`}
          accessible={true}
          key="sup_fee"
          style={getDescriber().getReceiptStyles().labelStyle}
          value={this.props.subtotal}
          currency={AccountStore.getCurrency()}
        />,
      );
    }

    if (this.props.deposit > 0) {
      labels.push(
        <AVText
          maxFontSizeMultiplier={1.0}
          accessibilityElementsHidden={true}
          importantForAccessibility="no-hide-descendants"
          style={getDescriber().getReceiptStyles().labelStyle}
          key="deposit"
        >
          {Localized.Labels.deposit}
        </AVText>,
      );
      amounts.push(
        <AVFormattedCurrency
          style={getDescriber().getReceiptStyles().labelStyle}
          value={this.props.deposit}
          accessible={true}
          accessibilityLabel={`${Localized.Labels.deposit} $${this.props.deposit}`}
          accessibilityRole="text"
          aria-label={`${Localized.Labels.deposit} $${this.props.deposit}, text`}
          key="deposit"
          currency={AccountStore.getCurrency()}
        />,
      );
    }

    if (this.props.split) {
      if (this.props.split) {
        let label = Localized.Labels.account_balance;

        if (Settings.buildType === BuildTypes.pantry) {
          label = Localized.Labels.remaining_credit;
        }

        labels.push(
          <AVText
            maxFontSizeMultiplier={1.0}
            accessibilityElementsHidden={true}
            importantForAccessibility="no-hide-descendants"
            style={getDescriber().getReceiptStyles().labelStyle}
            key="remaininBalance"
          >
            {label}
          </AVText>,
        );
        amounts.push(
          <AVFormattedCurrency
            style={getDescriber().getReceiptStyles().labelStyle}
            value={this.props.balance * -1}
            accessible={true}
            accessibilityLabel={`${label} $${this.props.balance * -1}`}
            accessibilityRole="text"
            aria-label={`${label} $${this.props.balance * -1}, text`}
            key="remaininBalance"
            currency={AccountStore.getCurrency()}
          />,
        );
      }
    }

    if (this.props.split) {
      labels.push(
        <AVText
          maxFontSizeMultiplier={1.0}
          style={getDescriber().getReceiptStyles().labelStyle}
          accessibilityElementsHidden={true}
          importantForAccessibility="no-hide-descendants"
          key="totalDue"
        >
          {Localized.Labels.total_due} ({this.props.ccDisplay})
        </AVText>,
      );
      amounts.push(
        <AVFormattedCurrency
          style={getDescriber().getReceiptStyles().labelStyle}
          value={this.props.totalDue}
          accessible={true}
          accessibilityLabel={`${Localized.Labels.total_due} (${this.props.ccDisplay}) $${this.props.totalDue}`}
          accessibilityRole="text"
          aria-label={`${Localized.Labels.total_due} (${this.props.ccDisplay}) $${this.props.totalDue}, text`}
          key="totalDue"
          currency={AccountStore.getCurrency()}
        />,
      );
    }

    return (
      <View style={getDescriber().getReceiptStyles().canteenRow}>
        <View>{labels}</View>
        <View>{amounts}</View>
      </View>
    );
  }

  render() {
    const currency = AccountStore.getCurrency();
    const totalAmt = this.computeTotalAmount();
    const receiptItems = this.props?.items?.map((item, index) => (
      <ReceiptItem
        desc={item.desc}
        amount={item.price}
        key={`${index}`}
        accessible={true}
        accessibilityLabel={`${item.name}`}
        aria-label={`${item.name}`}
      />
    ));
    return (
      <BaseSummaryScreen
        previousRoute={this.state.previousRoute}
        accessibilityLabel={Localized.Buttons.back_arrow}
        aria-label={Localized.Buttons.back_arrow}
        accessibilityHint={`Press to navigate back to the ${this.state.previousRoute} screen`}
        strings={Localized}
        titleText={Localized.Buttons.receipt}
        returnRoute={this.props.returnRoute}
        insideTab={this.props.insideTab}
        deviceId={this.props.deviceId}
        showReportBtn
      >
        {Settings.isRefive && this.renderHeader()}
        <DashedLine
          dashGap={PixelRatio.roundToNearestPixel(4)}
          dashLength={PixelRatio.roundToNearestPixel(5)}
          dashThickness={PixelRatio.roundToNearestPixel(1)}
          dashColor={Styles.inActiveDotColor}
          style={[
            getDescriber().getReceiptStyles().dashStyle,
            {
              marginTop: -1 * Styles.Spacing.m2,
            },
          ]}
        />
        {receiptItems}
        <DashedLine
          dashGap={PixelRatio.roundToNearestPixel(4)}
          dashLength={PixelRatio.roundToNearestPixel(5)}
          dashThickness={PixelRatio.roundToNearestPixel(1)}
          dashColor={Styles.inActiveDotColor}
          style={[
            getDescriber().getReceiptStyles().dashStyle,
            {
              marginTop: -1 * Styles.Spacing.m2,
            },
          ]}
        />
        {this.renderSummary()}
        {this.props.split ? null : (
          <>
            <DashedLine
              dashGap={PixelRatio.roundToNearestPixel(4)}
              dashLength={PixelRatio.roundToNearestPixel(5)}
              dashThickness={PixelRatio.roundToNearestPixel(1)}
              dashColor={Styles.inActiveDotColor}
              style={[getDescriber().getReceiptStyles().dashStyle]}
            />
            <View style={getDescriber().getReceiptStyles().canteenRow}>
              <AVText
                maxFontSizeMultiplier={1.0}
                style={getDescriber().getReceiptStyles().totalLabel}
                accessibilityElementsHidden={true}
                importantForAccessibility="no-hide-descendants"
                key="due"
              >
                {Localized.Labels.total}
              </AVText>

              <AVFormattedCurrency
                style={getDescriber().getReceiptStyles().totalLabel}
                accessible={true}
                accessibilityLabel={`${Localized.Labels.total} $${totalAmt}`}
                accessibilityRole="text"
                aria-label={`${Localized.Labels.total} $${totalAmt}, text`}
                key="due"
                value={totalAmt}
                currency={currency}
              />
            </View>
            {!Settings.isRefive() && (
              <View style={getDescriber().getReceiptStyles().canteenRow}>
                <AVText
                  maxFontSizeMultiplier={1.0}
                  style={
                    getDescriber().getReceiptStyles().remainingBalanceLabel
                  }
                  accessibilityElementsHidden={true}
                  importantForAccessibility="no-hide-descendants"
                  key="remainingBalance"
                >
                  {Localized.Labels.remaining_balance}
                </AVText>

                <AVFormattedCurrency
                  style={
                    getDescriber().getReceiptStyles().remainingBalanceLabel
                  }
                  accessible={true}
                  accessibilityLabel={`${Localized.Labels.remaining_balance} $${this.props.balance}`}
                  accessibilityRole="text"
                  aria-label={`${Localized.Labels.remaining_balance} $${this.props.balance}, text`}
                  key="remainingBalance"
                  value={this.props.balance}
                  currency={currency}
                />
              </View>
            )}
          </>
        )}
      </BaseSummaryScreen>
    );
  }
}

export default withForwardedNavigationParams<ReceiptScreenProps>()(
  withGlobalize(ReceiptScreen),
);
